import { requireAll } from './loader';
import { RFEModule, RFEModuleUiInstance} from './module';

export interface IDep {
    dep: Array<string>;
    test?: (context: HTMLElement) => boolean;
}

/**
 * Class that is uset to manage all JS modules
 * it keeps track of the loaded modules and provides the functionality to load/unload/init modules for the specified
 * context
 */
export class ModuleManager {
    /** @prop dependencies - all the supported modules and it's testing methods */
    private dependencies: {
        [phase: string]: Array<IDep>;
    } = {};

    /** @prop rfeModules - list of all loaded modules that supports init and deinit */
    private rfeModules: {
        [name: string]: RFEModule;
    } = {};

    /**
     * Add dependencies to the list of supported modules
     * @param deps - array of module definition
     * @param phase - phase when the module would be loaded
     */
    addDependencies(deps: Array<IDep>, phase: number | string = 0) {
        const phaseName = typeof phase === 'number' ? `init_${phase}` : phase;
        const currentDeps = typeof this.dependencies[phaseName] === 'undefined' ? [] : this.dependencies[phaseName];
        this.dependencies[phaseName] = [...currentDeps, ...deps];
    }

    /**
     * Init method for the specified context
     * It check the context and runs all test methods for all supported modules. If the test method passes, the module
     * is then loaded and initialized for the specified context
     * @param context - The HTMLElement we want to init modules for
     */
    async init(context: HTMLElement) {
        const phaseNames = Object.keys(this.dependencies).sort();
        const autoInitRE = /^init(_\d+)?$/;
        for (const phaseName of phaseNames) {
            if (autoInitRE.test(phaseName)) {
                await this.initPhase(phaseName, context);
            }
        }
    }

    /**
     * Deinit method for the specified context
     * It calls the deinit method for all initialized modules and passes the context as an argument
     * @param context - The HTMLElement we want to de-init modules in
     */
    deinit(context: HTMLElement) {
        for (const moduleName of Object.keys(this.rfeModules)) {
            this.deinitModule(moduleName, context);
        }
    }

    /**
     * Find the loaded module by name
     * @param moduleName - name of the module
     * @throws Error when the module is not found
     */
    getModule(moduleName: string) {
        if (typeof this.rfeModules[moduleName] === 'undefined') {
            throw new Error(`Module ${moduleName} not found. It hasn't been initialized yet.`);
        }
        return this.rfeModules[moduleName];
    }

    /**
     * Public alias for the private initPhase method
     * @see initPhase
     */
    async trigger(phase: string, context: HTMLElement) {
        return this.initPhase(phase, context);
    }

    /**
     * Runs another phase of the load process
     * @param phase - the phase name
     * @param context - The HTMLElement we want to init modules for
     */
    private async initPhase(phase: string, context: HTMLElement) {
        const moduleNames = this.getActiveModuleNames(phase, context);
        const modules = await requireAll(moduleNames);

        for (const moduleName of moduleNames) {
            this.initModule(moduleName, modules[moduleName], context);
        }

        if ( phase === 'pageFullLoad' ) {
            document.body.classList.add('js-loaded');
        }
    }

    /**
     * Initialize one module for the provided context
     * @param moduleName - name of the module
     * @param module - the module itself
     * @param context - The HTMLElement we want to init modules for
     */
    private initModule(moduleName: string, module: any, context: HTMLElement) {
        if (typeof this.rfeModules[moduleName] !== 'undefined') {
            const instance = this.rfeModules[moduleName];
            instance.init(context);
        }
        else if (module.default && module.default.isRFEModule) {
            const instance = new module.default();
            instance.init(context);
            this.rfeModules[moduleName] = instance;
        }
    }

    /**
     * De-initialize one module
     * @param moduleName - name of the loaded module
     * @param context  - The HTMLElement we want to de-init modules in
     */
    private deinitModule(moduleName: string, context: HTMLElement) {
        const instance = this.getModule(moduleName);
        instance.deinit(context);
    }

    /**
     * Returns the list of module names that should be initialized for the provided context based on its test method
     * @param phase - phase name
     * @param context - The HTMLElement we want to init modules for
     */
    private getActiveModuleNames(phase: string, context: HTMLElement): Array<string> {
        const moduleNames: Array<string> = [];
        const deps = this.dependencies[phase];
        if (deps && deps.length) {
            for (const dep of deps) {
                if (typeof dep.test === 'undefined' || dep.test(context)) {
                    for (const singleDep of dep.dep) {
                        moduleNames.push(singleDep);
                    }
                }
            }
        }
        return moduleNames;
    }
}

const moduleManager = new ModuleManager();
export default moduleManager;



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./source/js/helpers/moduleManager.ts